import { k8sStatusLabel } from './k8sStatusLabel'

/**
 * Catálogo de indicadores del bloque Portal.
 * `id` debe coincidir con el que emite el backend.
 */
export const PORTAL_INDICATOR_HELP = {
  portal_front_signin: {
    title: 'Shell /signin',
    what: 'HTML del login en CloudFront/S3. Si cae, el usuario no llega a ver el formulario aunque el API esté bien.',
    check: 'GET https://portal.smartcfo.cl/signin',
    readyWhen: 'HTTP 200, HTML con #root (CRA)',
    unavailableWhen: 'Timeout, status ≠ 200, o HTML sin shell del SPA',
  },
  portal_front_home: {
    title: 'Shell /',
    what: 'Misma SPA en la raíz. Confirma que CloudFront enruta / además de /signin.',
    check: 'GET https://portal.smartcfo.cl/',
    readyWhen: 'HTTP 200, HTML con #root',
    unavailableWhen: 'Timeout, status ≠ 200, o HTML sin shell',
  },
  portal_front_assets: {
    title: 'Assets JS/CSS',
    what: 'Bundles /static/js y /static/css referenciados por el shell. Si el HTML carga pero el JS no, la pantalla queda en blanco y ninguna interacción funciona.',
    check: 'Parse del shell → HEAD de main.*.js y main.*.css',
    readyWhen: 'Todos los assets propios responden HTTP 200',
    notReadyWhen: 'Assets OK pero lentos (≥2.5s)',
    unavailableWhen: 'Algún asset 4xx/5xx o timeout',
  },
  portal_front_graphql: {
    title: 'GraphQL (SPA)',
    what: 'Backend que usa el front para login, mutaciones y queries (server.smartcfo.cl/graphql). La mayoría de “interacciones fallidas” del portal son fallos aquí, no en el HTML estático.',
    check: 'POST { __typename } → data.__typename === Query',
    readyWhen: 'HTTP 200 y Query responde',
    unavailableWhen: 'Timeout, status ≠ 200, o body sin data.Query',
  },
  rabbit_ui: {
    title: 'Management UI',
    what: 'Shell HTML del panel RabbitMQ Management expuesto en rabbit.smartcfo.cl (ALB → servicio). Confirma que el edge y el UI del broker responden.',
    check: 'GET https://rabbit.smartcfo.cl/ → title RabbitMQ Management',
    readyWhen: 'HTTP 200 y HTML del Management',
    unavailableWhen: 'Timeout, status ≠ 200, o HTML sin título RabbitMQ',
  },
  rabbit_mgmt: {
    title: 'RabbitMQ API',
    what: 'Overview del broker vía Management API: conexiones, consumers y mensajes. Si responde, el broker está alcanzable. El backlog de colas se mide en «Colas clave» (Atención), no aquí.',
    check: 'GET https://rabbit.smartcfo.cl/api/overview (Basic auth)',
    readyWhen: 'API responde con conexiones > 0',
    notReadyWhen: 'API OK pero 0 conexiones',
    unavailableWhen: 'API caída, timeout o auth fallida',
  },
  rabbit_queues: {
    title: 'Colas clave',
    what: 'Tabla Colas Rabbit: Ready = msgs en espera (backlog). Alerta Backlog solo en colas con basic.consume cuando Ready ≥ umbral. Colas get/poll (sii-queue, sii-priority, sii-resume, tax-documents) muestran Ready pero no alertan. Sin consumer = Bloqueante (solo consume).',
    check: 'GET https://rabbit.smartcfo.cl/api/queues (Basic auth)',
    readyWhen: 'OK: cola presente; get/poll siempre OK en Alerta (mirar Ready en la tabla)',
    notReadyWhen: 'Atención: Alerta = Backlog en colas consume',
    unavailableWhen:
      'Bloqueante: ready > 0 sin consumer (solo colas consume, no get/poll)',
  },
  rabbit_memory: {
    title: 'Rabbit memoria',
    what: 'Uso de memoria Erlang por nodo. Si mem_alarm=true, Rabbit bloquea publishers: el portal deja de encolar. Eso es Bloqueante.',
    check: 'GET https://rabbit.smartcfo.cl/api/nodes (Basic auth)',
    readyWhen: 'Nodos running, sin mem_alarm/disk_alarm, mem% bajo umbral',
    notReadyWhen: 'Atención: mem% ≥ RABBITMQ_MEM_WARN_PCT (default 80)',
    unavailableWhen:
      'Bloqueante: mem_alarm / disk_alarm, nodo no running, o mem% ≥ 90',
  },
  k8s_nodes: {
    title: 'Nodos EKS',
    what: 'Capacidad de los nodos AWS: Ready, Pressure, CPU/Mem vs allocatable. Un nodo saturado o NotReady es Bloqueante.',
    check: 'listNode + metrics.k8s.io/nodes (ClusterRole en health-rbac.yaml)',
    readyWhen: 'Ready=True, sin Pressure, CPU/Mem < 80% allocatable',
    notReadyWhen: 'Atención: CPU o Mem ≥ 80% allocatable',
    unavailableWhen: 'Bloqueante: Ready≠True, Pressure, o recursos ≥ 95%',
  },
  vultr_ops: {
    title: 'Instances',
    what: 'Inventario de Instances de la cuenta Vultr. La tabla muestra los tres estados oficiales: status, power_status y server_status.',
    check: 'GET https://api.vultr.com/v2/instances (solo lectura)',
    readyWhen: 'status active, power_status running y server_status ok',
    notReadyWhen:
      'status pending o resizing, o server_status installingbooting, isomounting o none',
    unavailableWhen:
      'status suspended, power_status stopped, o server_status locked',
    unknownWhen: 'Sin VULTR_API_KEY, o GET /v2/instances no autorizado',
    terms: {
      readyWhen: 'active cuando',
      notReadyWhen: 'Atención cuando',
      unavailableWhen: 'Bloqueante cuando',
      status: 'Estado',
    },
  },
  k8s_fleet: {
    title: 'Flota crítica',
    what: 'Deployments/StatefulSets del path del portal (tier critical). Ready parcial = Atención; ready 0 = Bloqueante. Usa Logs del servicio afectado.',
    check: 'K8s readyReplicas + conditions CrashLoop/OOM/ImagePull',
    readyWhen: 'Todos los críticos con ready == desired',
    notReadyWhen: 'Atención: degradado (ready parcial) o recursos altos',
    unavailableWhen: 'Bloqueante: ready 0 con desired > 0',
  },
  portal_front: {
    title: 'Front portal (legacy)',
    what: 'Indicador antiguo; reemplazado por Shell /signin + assets + GraphQL.',
    check: 'GET https://portal.smartcfo.cl/signin',
    readyWhen: 'HTTP 200 con #root',
    unavailableWhen: 'Timeout o HTML inválido',
  },
  mcp_health: {
    title: 'Portal MCP /health',
    what: 'Liveness del proceso MCP expuesto en mcp.smartcfo.cl (usado por probes de K8s y ALB).',
    check: 'GET https://mcp.smartcfo.cl/health',
    readyWhen: 'HTTP 200 y JSON con status: "ok"',
    unavailableWhen: 'Timeout, error de red, o respuesta distinta de ok',
  },
  oauth_discovery: {
    title: 'OAuth discovery',
    what: 'Metadata OAuth 2.1 (RFC 8414) que claude.ai necesita para el flujo "Add custom connector".',
    check: 'GET https://mcp.smartcfo.cl/.well-known/oauth-authorization-server',
    readyWhen: 'HTTP 200 y JSON con authorization_endpoint',
    unavailableWhen: 'Timeout, error de red, o metadata incompleta',
  },
  public_api: {
    title: 'API pública',
    what: 'Upstream de las tools MCP: smart_public_api (api.smartcfo.cl). Verde aquí no garantiza que public_api pueda llamar a smart_back.',
    check: 'GET https://api.smartcfo.cl/api (Swagger / liveness)',
    readyWhen: 'HTTP 200',
    unavailableWhen: 'Timeout, error de red, o status ≠ 200',
  },
  public_api_deps: {
    title: 'API → smart_back',
    what: 'Cadena rota típica: INTERNAL_API_SECRET o SMART_BACK_INTERNAL_URL mal puestos. public_api llama GET /internal/ping. Si falla, las tools MCP ven /api en verde pero no leen datos.',
    check: 'GET https://api.smartcfo.cl/health/deps → smart_back /internal/ping',
    readyWhen: 'HTTP 200 y smartBack.ok',
    unavailableWhen:
      '503 / secret ausente / URL interna incorrecta. 404 = endpoint aún no desplegado (unknown).',
  },
  smart_back_ready: {
    title: 'smart_back ready',
    what: 'Readiness del monolito (Mongo ping). Paths EERR/balance del MCP dependen de este servicio vía API interna.',
    check: 'GET https://server.smartcfo.cl/health/ready',
    readyWhen: 'HTTP 200 (idealmente body.status: "ok")',
    unavailableWhen: 'Timeout, error de red, o readiness fallido',
  },
  mongo_memory: {
    title: 'Mongo memoria',
    what: 'Uso de System Memory del host Atlas (cluster main). Misma señal que las alertas de alto uso de RAM en Atlas; no es el ping de conexión ni memoria de pods EKS.',
    check:
      'GET Atlas …/groups/{groupId}/processes/{host:port}/measurements (SYSTEM_MEMORY_USED + AVAILABLE)',
    readyWhen: 'Memoria host < ATLAS_MEM_WARN_PCT (default 80%)',
    notReadyWhen: 'Atención: mem% ≥ ATLAS_MEM_WARN_PCT (default 80)',
    unavailableWhen: 'Bloqueante: mem% ≥ ATLAS_MEM_ERROR_PCT (default 90)',
    unknownWhen:
      'Sin ATLAS_PUBLIC_KEY / ATLAS_PRIVATE_KEY / ATLAS_GROUP_ID, o Metrics API no responde',
  },
  oauth_login: {
    title: 'Login OAuth',
    what: '¿La ruta de login del connector responde ahora? GET /authorize sin PKCE debe devolver 400 (handler vivo). No cuenta logins ni logs Mongo.',
    check: 'GET https://mcp.smartcfo.cl/authorize → 400 + unsupported_response_type',
    readyWhen: 'HTTP 400 con body que incluye unsupported_response_type',
    unavailableWhen: 'Timeout, error de red, o respuesta distinta del 400 esperado',
  },
  oauth_sessions: {
    title: 'Sesiones MCP',
    what: 'Sesiones OAuth activas en portal_mcp_sessions (tokens de claude.ai hacia el MCP).',
    check: 'Count sessions con expiresAt > now; lastUsedAt en la última hora',
    readyWhen: 'La query a Mongo responde (el conteo puede ser 0)',
    unknownWhen: 'No se pudo leer la colección portal_mcp_sessions',
  },
  mcp_traffic: {
    title: 'Tráfico claude_mcp',
    what: 'Llamadas a smart_public_api etiquetadas origen=claude_mcp (tools del connector).',
    check: 'Aggregate api_logs / apilogs donde origen=claude_mcp (1h y 24h)',
    readyWhen: 'Hay tráfico y ratio 5xx < 5% (con ≥10 req/1h)',
    notReadyWhen: 'Sin tráfico, o 5xx entre 5% y 15%',
    unavailableWhen: '5xx ≥ 15% en la última hora (≥10 req)',
  },
}

export function getPortalCardHelp(ind, statusLabel) {
  const help = PORTAL_INDICATOR_HELP[ind?.id] || {
    title: ind?.label || 'Indicador',
    what: 'Indicador de salud del portal.',
    check: '—',
    readyWhen: '—',
  }
  const terms = help.terms || {}

  const rows = [
    { label: 'Qué es', value: help.what },
    { label: 'Check', value: help.check },
    { label: terms.readyWhen || 'Ready cuando', value: help.readyWhen },
  ]
  if (help.notReadyWhen) {
    rows.push({
      label: terms.notReadyWhen || 'NotReady cuando',
      value: help.notReadyWhen,
    })
  }
  if (help.unavailableWhen) {
    rows.push({
      label: terms.unavailableWhen || 'Unavailable cuando',
      value: help.unavailableWhen,
    })
  }
  if (help.unknownWhen) {
    rows.push({ label: 'Unknown cuando', value: help.unknownWhen })
  }
  rows.push({
    label: terms.status || 'Status',
    value: statusLabel || k8sStatusLabel(ind?.status),
  })
  if (ind?.summary) rows.push({ label: 'Ahora', value: ind.summary })
  if (ind?.detail) rows.push({ label: 'Detail', value: ind.detail })
  if (ind?.checkedAt) {
    try {
      rows.push({
        label: 'checkedAt',
        value: new Date(ind.checkedAt).toLocaleString('es-CL'),
      })
    } catch {
      // ignore
    }
  }

  return {
    title: help.title || ind?.label || 'Indicador',
    rows,
  }
}

/** @deprecated prefer getPortalCardHelp + popover */
export function buildPortalCardTooltip(ind) {
  const { title, rows } = getPortalCardHelp(ind)
  return [title, '', ...rows.map((r) => `${r.label}: ${r.value}`)].join('\n')
}

